import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  MessageCircle, 
  Plus, 
  Send, 
  Clock,
  CheckCircle2,
  User,
  Loader2,
  Inbox,
  ArrowLeft,
  Sparkles,
  UserCheck,
  HelpCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DashboardLayout from "../components/DashboardLayout";
import TrialBlocker from "../components/TrialBlocker";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  open: { label: "Abierta", color: "bg-blue-100 text-blue-700" },
  assigned: { label: "Asignada", color: "bg-purple-100 text-purple-700" },
  resolved: { label: "Resuelta", color: "bg-emerald-100 text-emerald-700" },
  closed: { label: "Cerrada", color: "bg-navy-100 text-navy-600" }
};

const relatedToOptions = [
  { value: "general", label: "Consulta General" },
  { value: "documents", label: "Documentos" },
  { value: "ds160", label: "Formulario DS-160" },
  { value: "interview", label: "Entrevista" },
  { value: "appointment", label: "Cita Consular" },
  { value: "billing", label: "Facturación" }
];

const ChatPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("oficial"); // "oficial" or "consultas"
  
  // Chat with oficial
  const [oficialInfo, setOficialInfo] = useState(null);
  const [oficialMessages, setOficialMessages] = useState([]);
  const [loadingOficial, setLoadingOficial] = useState(true);
  
  // Consultas (Centro de Mensajes)
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  // Common
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [newRelatedTo, setNewRelatedTo] = useState("general");
  const [creating, setCreating] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchOficialChat();
    fetchConversations();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [oficialMessages, conversationMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Fetch chat with assigned oficial
  const fetchOficialChat = async () => {
    setLoadingOficial(true);
    try {
      // Get case to find assigned oficial
      const caseRes = await axios.get(`${API}/cases/my-case`);
      const myCase = caseRes.data;
      
      if (myCase && myCase.oficial_id) {
        setOficialInfo({
          id: myCase.oficial_id,
          name: myCase.oficial_name || "Tu Oficial",
          email: null
        });
        
        // Get chat messages with this oficial
        const chatRes = await axios.get(`${API}/chat/with/${myCase.oficial_id}`);
        setOficialMessages(chatRes.data.messages || []);
      } else {
        setOficialInfo(null);
      }
    } catch (error) {
      console.error("Error fetching oficial chat:", error);
      setOficialInfo(null);
    } finally {
      setLoadingOficial(false);
    }
  };

  // Fetch consultas
  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/conversations`);
      setConversations(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingConversations(false);
    }
  };

  const fetchConversationMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const res = await axios.get(`${API}/conversations/${conversationId}`);
      setSelectedConversation(res.data.conversation);
      setConversationMessages(res.data.messages);
    } catch (error) {
      toast.error("Error al cargar mensajes");
    } finally {
      setLoadingMessages(false);
    }
  };

  // Send message to oficial
  const sendMessageToOficial = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !oficialInfo) return;

    setSending(true);
    try {
      await axios.post(`${API}/chat/messages`, {
        receiver_id: oficialInfo.id,
        content: newMessage
      });
      setNewMessage("");
      // Refresh messages
      const chatRes = await axios.get(`${API}/chat/with/${oficialInfo.id}`);
      setOficialMessages(chatRes.data.messages || []);
      toast.success("Mensaje enviado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  // Send message to consulta
  const sendConversationMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const res = await axios.post(
        `${API}/conversations/${selectedConversation.id}/messages`,
        { content: newMessage }
      );
      setConversationMessages([...conversationMessages, res.data]);
      setNewMessage("");
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  const createNewConversation = async () => {
    if (!newSubject.trim()) {
      toast.error("Ingresa un tema para tu consulta");
      return;
    }

    setCreating(true);
    try {
      const res = await axios.post(`${API}/conversations`, {
        subject: newSubject,
        related_to: newRelatedTo
      });
      setConversations([res.data, ...conversations]);
      setShowNewConversation(false);
      setNewSubject("");
      setNewRelatedTo("general");
      toast.success("Consulta creada. Un asesor te responderá pronto.");
    } catch (error) {
      toast.error("Error al crear consulta");
    } finally {
      setCreating(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Ayer ' + date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loadingOficial && loadingConversations) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <TrialBlocker feature="Chat con Asesor">
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="chat-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
            Centro de Comunicación
          </h1>
          <p className="text-navy-600">
            Comunícate con tu oficial migratorio o crea consultas generales
          </p>
        </motion.div>

        {/* Tab Buttons */}
        <div className="flex gap-3 mb-6">
          <Button
            variant={activeTab === "oficial" ? "default" : "outline"}
            onClick={() => setActiveTab("oficial")}
            className={activeTab === "oficial" ? "bg-purple-600 hover:bg-purple-700" : ""}
          >
            <UserCheck className="w-4 h-4 mr-2" />
            Chat con mi Oficial
          </Button>
          <Button
            variant={activeTab === "consultas" ? "default" : "outline"}
            onClick={() => setActiveTab("consultas")}
            className={activeTab === "consultas" ? "bg-blue-600 hover:bg-blue-700" : ""}
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Consultas Generales
          </Button>
        </div>

        {/* Chat with Oficial */}
        {activeTab === "oficial" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="card-elevated overflow-hidden h-[500px] flex flex-col"
          >
            {loadingOficial ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : oficialInfo ? (
              <>
                {/* Header */}
                <div className="p-4 border-b border-navy-100 bg-gradient-to-r from-purple-50 to-indigo-50">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                      {oficialInfo.name?.charAt(0) || "O"}
                    </div>
                    <div>
                      <h2 className="font-semibold text-navy-900">{oficialInfo.name}</h2>
                      <p className="text-sm text-navy-500">Tu Oficial Migratorio Asignado</p>
                    </div>
                    <span className="ml-auto px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                      Conectado
                    </span>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-navy-50/30">
                  {oficialMessages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="w-12 h-12 text-navy-300 mx-auto mb-3" />
                      <p className="text-navy-600 font-medium">¡Inicia la conversación!</p>
                      <p className="text-navy-500 text-sm">Escribe un mensaje a tu oficial</p>
                    </div>
                  ) : (
                    oficialMessages.map((msg) => {
                      const isMe = msg.sender_id === user?.id;
                      return (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                        >
                          <div className={`max-w-[75%]`}>
                            <div className={`p-3 rounded-2xl ${
                              isMe 
                                ? "bg-purple-600 text-white rounded-br-md" 
                                : "bg-white text-navy-800 rounded-bl-md shadow-sm border border-navy-100"
                            }`}>
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                            <p className={`text-xs mt-1 ${isMe ? "text-right" : ""} text-navy-400`}>
                              {formatTime(msg.sent_at || msg.created_at)}
                            </p>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={sendMessageToOficial} className="p-4 border-t border-navy-100 bg-white">
                  <div className="flex gap-3">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Escribe un mensaje..."
                      className="flex-1"
                      disabled={sending}
                      data-testid="oficial-chat-input"
                    />
                    <Button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-8">
                <div>
                  <User className="w-16 h-16 text-navy-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-navy-700 mb-2">
                    Sin Oficial Asignado
                  </h3>
                  <p className="text-navy-500 text-sm max-w-sm">
                    Aún no tienes un oficial migratorio asignado. Una vez asignado, podrás comunicarte directamente aquí.
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Consultas Generales */}
        {activeTab === "consultas" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid lg:grid-cols-3 gap-6 h-[500px]"
          >
            {/* Conversations List */}
            <div className="card-elevated overflow-hidden flex flex-col">
              <div className="p-4 border-b border-navy-100 flex items-center justify-between">
                <h2 className="font-semibold text-navy-900">Mis Consultas</h2>
                <Button
                  size="sm"
                  onClick={() => setShowNewConversation(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {loadingConversations ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <Inbox className="w-10 h-10 text-navy-300 mx-auto mb-2" />
                    <p className="text-navy-500 text-sm">No tienes consultas</p>
                    <Button
                      size="sm"
                      className="mt-3 bg-blue-600 hover:bg-blue-700"
                      onClick={() => setShowNewConversation(true)}
                    >
                      Nueva Consulta
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-navy-100">
                    {conversations.map((conv) => (
                      <div
                        key={conv.id}
                        onClick={() => fetchConversationMessages(conv.id)}
                        className={`p-4 cursor-pointer transition-colors hover:bg-navy-50 ${
                          selectedConversation?.id === conv.id ? "bg-blue-50 border-l-4 border-blue-500" : ""
                        }`}
                      >
                        <h3 className="font-medium text-sm truncate text-navy-900">
                          {conv.subject}
                        </h3>
                        <p className="text-xs text-navy-500 mt-1">
                          {conv.assigned_name 
                            ? `Tu asesor: ${conv.assigned_name}` 
                            : "En espera de asignación"}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig[conv.status]?.color}`}>
                            {statusConfig[conv.status]?.label}
                          </span>
                          <span className="text-xs text-navy-400">
                            {formatTime(conv.last_message_at || conv.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className="lg:col-span-2 card-elevated overflow-hidden flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-navy-100 bg-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="font-semibold text-navy-900">{selectedConversation.subject}</h2>
                        <p className="text-sm text-navy-500">
                          {selectedConversation.assigned_name 
                            ? `Tu asesor: ${selectedConversation.assigned_name}` 
                            : "En espera de asignación - puedes seguir escribiendo"}
                        </p>
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full ${statusConfig[selectedConversation.status]?.color}`}>
                        {statusConfig[selectedConversation.status]?.label}
                      </span>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-navy-50/30">
                    {loadingMessages ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                      </div>
                    ) : conversationMessages.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageCircle className="w-12 h-12 text-navy-300 mx-auto mb-3" />
                        <p className="text-navy-500">Sin mensajes aún</p>
                      </div>
                    ) : (
                      conversationMessages.map((msg) => {
                        const isMe = msg.sender_id === user?.id;
                        return (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                          >
                            <div className={`max-w-[75%]`}>
                              <p className={`text-xs mb-1 ${isMe ? "text-right text-navy-500" : "text-navy-500"}`}>
                                {isMe ? "Tú" : msg.sender_name}
                              </p>
                              <div className={`p-3 rounded-2xl ${
                                isMe 
                                  ? "bg-blue-600 text-white rounded-br-md" 
                                  : "bg-white text-navy-800 rounded-bl-md shadow-sm"
                              }`}>
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              </div>
                              <p className={`text-xs mt-1 ${isMe ? "text-right" : ""} text-navy-400`}>
                                {formatTime(msg.sent_at)}
                              </p>
                            </div>
                          </motion.div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  {selectedConversation.status !== "closed" && (
                    <form onSubmit={sendConversationMessage} className="p-4 border-t border-navy-100 bg-white">
                      <div className="flex gap-3">
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Escribe tu mensaje..."
                          className="flex-1"
                          disabled={sending}
                          data-testid="chat-input"
                        />
                        <Button
                          type="submit"
                          disabled={sending || !newMessage.trim()}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          {sending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </form>
                  )}
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-center p-8">
                  <div>
                    <MessageCircle className="w-16 h-16 text-navy-200 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-navy-700 mb-2">
                      Selecciona una consulta
                    </h3>
                    <p className="text-navy-500 text-sm">
                      O crea una nueva para recibir ayuda
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* New Conversation Modal */}
        <AnimatePresence>
          {showNewConversation && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowNewConversation(false)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl p-6 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold text-navy-900 mb-4">Nueva Consulta</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-navy-600 mb-1 block">Tema de tu consulta</label>
                    <Input
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      placeholder="Ej: Pregunta sobre mi DS-160"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-navy-600 mb-1 block">Relacionado con</label>
                    <Select value={newRelatedTo} onValueChange={setNewRelatedTo}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {relatedToOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowNewConversation(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      onClick={createNewConversation}
                      disabled={creating}
                    >
                      {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear Consulta"}
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
    </TrialBlocker>
  );
};

export default ChatPage;
